import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullseye, faCheck } from "@fortawesome/free-solid-svg-icons";
import PricingFeatureIllustration from "../Components/PricingFeatureIllustrations";
import "../assets/css/Pricing.css";

/* ------------------------------------------------------------------ *
 * Owner settings
 * ------------------------------------------------------------------ */

/* Set to "excluding VAT" or "including VAT (TVA 20%)" once the tax regime is settled. */
const TAX_NOTE = "";

/* ------------------------------------------------------------------ *
 * Pricing model
 *
 * This page communicates the PRICE PER TABLE only. It deliberately never
 * prints a contract total - the club owner multiplies by his own number of
 * tables himself. Do not add total or example figures back in.
 * ------------------------------------------------------------------ */

/* Rates are written out per period rather than derived from a percentage: the
   bands no longer discount by the same amount, so no single multiplier
   reproduces this grid. Edit the numbers here and the cards, the comparison
   table and every "save" figure follow. All values are whole dollars. */
const TIERS = [
    {
        id: "small",
        label: "1 – 3 tables",
        tagline: "Small clubs and cafés",
        rates: { "3m": 12, "6m": 10, "12m": 9 },
    },
    {
        id: "medium",
        label: "4 – 9 tables",
        tagline: "The usual club size",
        rates: { "3m": 10, "6m": 9, "12m": 8 },
        featured: true,
    },
    {
        id: "large",
        label: "10 tables and more",
        tagline: "Large halls and academies",
        rates: { "3m": 8, "6m": 7, "12m": 6 },
    },
];

const PERIODS = [
    { id: "3m", label: "3 months", full: "3 months" },
    { id: "6m", label: "6 months", full: "6 months" },
    { id: "12m", label: "1 year", full: "12 months" },
];

/* The shortest period is the undiscounted one every saving is measured against. */
const BASE_PERIOD_ID = "3m";

const rateFor = (tier, period) => tier.rates[period.id];
const baseRateFor = (tier) => tier.rates[BASE_PERIOD_ID];

/* A tier's own saving, used on its card. Bands differ — at 6 months the small
   band saves 17% while the usual band saves 10% — so a period cannot advertise
   one figure. The period switch and the table header show the best saving any
   band gets, labelled "up to", which is true for every band and overstates none. */
const discountFor = (tier, period) => 1 - rateFor(tier, period) / baseRateFor(tier);
const bestDiscountFor = (period) =>
    Math.max(...TIERS.map((tier) => discountFor(tier, period)));
const asPercent = (value) => Math.round(value * 100);

const INCLUDED = [
    {
        id: "score",
        title: "The score on your screens",
        description: "Every table has its screen. The score changes live as the game is played.",
    },
    {
        id: "tournaments",
        title: "Tournaments",
        description: "Build the bracket, then let the screens show every match and result.",
    },
    {
        id: "queues",
        title: "Table queues",
        description: "Customers see who is playing, who is next and how long the wait is.",
    },
    {
        id: "streaming",
        title: "Live streaming link",
        description: "Share one link so every match can be followed outside the club.",
    },
    {
        id: "stats",
        title: "Club statistics",
        description: "See hours played, busiest tables, best players and revenue per table.",
    },
    {
        id: "unlimited",
        title: "Unlimited everything",
        description: "Players, tournaments and staff stay unlimited. Tables are all we count.",
    },
    {
        id: "pairing",
        title: "Pair any TV in minutes",
        description: "Pair each TV once. Android TV and webOS apps are included.",
    },
    {
        id: "players",
        title: "Your regular players saved",
        description: "Find regulars by name instead of retyping them every night.",
    },
    {
        id: "updates",
        title: "Updates included",
        description: "Every improvement and new feature arrives at no extra cost.",
    },
];
const Pricing = () => {
    const { user, loading } = useAuth();

    const [periodId, setPeriodId] = useState("12m");
    const period = PERIODS.find((item) => item.id === periodId) || PERIODS[0];

    return (
        <div className="pricing-page">
            <header className="pricing-topbar">
                <div className="pricing-container pricing-topbar-inner">
                    <Link to="/" className="pricing-brand">
                        <FontAwesomeIcon icon={faBullseye} className="pricing-brand-icon" />
                        <span className="pricing-brand-text">CueClub Manager</span>
                    </Link>
                    <div className="pricing-topbar-actions">
                        <Link to="/" className="pricing-topbar-link">
                            <span className="pricing-topbar-link-full">Back to site</span>
                        </Link>
                        {!loading && !user && (
                            <Link to="/login" className="pricing-btn pricing-btn-primary pricing-btn-sm">
                                Log in
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main>
                <section className="pricing-hero">
                    <div className="pricing-container pricing-hero-inner">
                        <span className="pricing-eyebrow">Pricing</span>
                        <h1 className="pricing-h1">
                            You only pay for <span className="pricing-h1-accent">your tables</span>
                        </h1>
                        <p className="pricing-lede">
                            One price per table, per month. The more tables you have, the lower the
                            price per table. Choose 6 months or 1 year and the price drops again.
                        </p>

                        <div
                            className="pricing-switch"
                            role="group"
                            aria-label="How long you subscribe"
                        >
                            {PERIODS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`pricing-switch-option ${
                                        item.id === period.id ? "is-active" : ""
                                    }`}
                                    onClick={() => setPeriodId(item.id)}
                                    aria-pressed={item.id === period.id}
                                >
                                    {item.label}
                                    {bestDiscountFor(item) > 0 && (
                                        <span className="pricing-switch-save">
                                            up to −{asPercent(bestDiscountFor(item))}%
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <p className="pricing-switch-note">
                            Paid once at the start, covering the full {period.full}. Your price per
                            table stays the same until the period ends.
                            {TAX_NOTE ? ` All prices are ${TAX_NOTE}.` : ""}
                        </p>
                        <p className="pricing-sr-only" aria-live="polite">
                            Prices updated for {period.full}. Any discount is included in the rates shown.
                        </p>
                    </div>
                </section>

                <section className="pricing-plans" aria-labelledby="pricing-plans-title">
                    <div className="pricing-container">
                        <h2 className="pricing-sr-only" id="pricing-plans-title">
                            Price per table by number of tables
                        </h2>

                        <div className="pricing-cards">
                            {TIERS.map((tier) => {
                                const rate = rateFor(tier, period);

                                return (
                                    <article
                                        key={tier.id}
                                        className={`pricing-card ${tier.featured ? "is-best" : ""}`}
                                    >
                                        <div className="pricing-card-badge-row">
                                            {tier.featured && (
                                                <span className="pricing-card-badge">
                                                    <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                                                    Most clubs
                                                </span>
                                            )}
                                        </div>

                                        <h3 className="pricing-card-tier">{tier.label}</h3>
                                        <p className="pricing-card-tagline">{tier.tagline}</p>

                                        <p className="pricing-rate">
                                            <span className="pricing-rate-num">{rate}</span>
                                            <span className="pricing-rate-cur">$</span>
                                        </p>
                                        <p className="pricing-rate-unit">per table, per month</p>

                                        <div className="pricing-was">
                                            {discountFor(tier, period) > 0 && (
                                                <>
                                                    <del aria-hidden="true">{baseRateFor(tier)} $</del>
                                                    <span className="pricing-sr-only">
                                                        Price without the discount: {baseRateFor(tier)} dollars
                                                        per table per month.
                                                    </span>
                                                    <span className="pricing-save">
                                                        Save {asPercent(discountFor(tier, period))}%
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        <ul className="pricing-card-list">
                                            <li>
                                                <FontAwesomeIcon icon={faCheck} className="pricing-check" aria-hidden="true" />
                                                <span>Every feature included, nothing costs extra</span>
                                            </li>
                                            <li>
                                                <FontAwesomeIcon icon={faCheck} className="pricing-check" aria-hidden="true" />
                                                <span>Unlimited players, matches and tournaments</span>
                                            </li>
                                            <li>
                                                <FontAwesomeIcon icon={faCheck} className="pricing-check" aria-hidden="true" />
                                                <span>Android, Android TV and webOS apps</span>
                                            </li>
                                            <li>
                                                <FontAwesomeIcon icon={faCheck} className="pricing-check" aria-hidden="true" />
                                                <span>Paid once for the whole period</span>
                                            </li>
                                        </ul>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="pricing-matrix" aria-labelledby="pricing-matrix-title">
                    <div className="pricing-container pricing-narrow">
                        <p className="pricing-section-kicker pricing-section-kicker-light">
                            Clear comparison
                        </p>
                        <h2 className="pricing-h2" id="pricing-matrix-title">
                            All prices side by side
                        </h2>
                        <p className="pricing-section-lede">
                            Price per table, per month, for every band and every subscription length.
                        </p>

                        <p className="pricing-table-scroll-hint" aria-hidden="true">
                            Swipe to compare every period →
                        </p>
                        <div
                            className="pricing-table-wrap"
                            role="region"
                            tabIndex={0}
                            aria-label="Scrollable pricing comparison"
                        >
                            <table className="pricing-table">
                                <caption className="pricing-sr-only">
                                    Price per table per month, by number of tables and subscription
                                    length
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Tables</th>
                                        {PERIODS.map((item) => (
                                            <th
                                                key={item.id}
                                                scope="col"
                                                className={item.id === period.id ? "is-selected" : ""}
                                            >
                                                {item.id === period.id && (
                                                    <span className="pricing-sr-only">Selected period: </span>
                                                )}
                                                {item.label}
                                                {bestDiscountFor(item) > 0 &&
                                                    ` (up to −${asPercent(bestDiscountFor(item))}%)`}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {TIERS.map((tier) => (
                                        <tr key={tier.id}>
                                            <th scope="row">{tier.label}</th>
                                            {PERIODS.map((item) => (
                                                <td
                                                    key={item.id}
                                                    className={
                                                        item.id === period.id ? "is-selected" : ""
                                                    }
                                                >
                                                    {rateFor(tier, item)} $
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section
                    className="pricing-included"
                    id="features-included"
                    aria-labelledby="pricing-included-title"
                >
                    <div className="pricing-container">
                        <p className="pricing-included-kicker">Everything you need · nothing held back</p>
                        <h2 className="pricing-h2" id="pricing-included-title">
                            Every plan includes everything
                        </h2>
                        <p className="pricing-section-lede">
                            There is no basic or premium version. Every club gets the complete
                            CueClub system from day one.
                        </p>

                        <ul className="pricing-included-grid">
                            {INCLUDED.map((feature, index) => (
                                <li
                                    key={feature.id}
                                    className="pricing-included-item"
                                    style={{ "--feature-order": index }}
                                >
                                    <div className="pricing-feature-visual">
                                        <PricingFeatureIllustration type={feature.id} />
                                    </div>
                                    <div className="pricing-feature-copy">
                                        <h3 className="pricing-included-title">{feature.title}</h3>
                                        <p className="pricing-included-desc">{feature.description}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="pricing-faq" aria-labelledby="pricing-faq-title">
                    <div className="pricing-container pricing-faq-layout">
                        <div className="pricing-faq-intro">
                            <p className="pricing-section-kicker">Straight answers</p>
                            <h2 className="pricing-h2 pricing-h2-left" id="pricing-faq-title">
                                Good to know
                            </h2>
                            <p className="pricing-faq-lede">
                                No hidden rules, automatic renewals or surprise extras.
                            </p>
                        </div>

                        <div className="pricing-faq-list">
                            <div className="pricing-faq-item">
                                <h3 className="pricing-faq-q">How is my price calculated?</h3>
                                <p className="pricing-faq-a">
                                    Find your band by the number of tables you have. That is your price
                                    per table, per month. The discount for 6 months and 1 year is already
                                    included in the price shown.
                                </p>
                            </div>

                            <div className="pricing-faq-item">
                                <h3 className="pricing-faq-q">
                                    I have 9 tables. Do I pay more than a club with 10?
                                </h3>
                                <p className="pricing-faq-a">
                                    No. You are never charged more than a club in the band above you. With
                                    9 tables you are billed at the 10 table price, so growing always costs
                                    you less per table and never more overall.
                                </p>
                            </div>

                            <div className="pricing-faq-item">
                                <h3 className="pricing-faq-q">When do I pay?</h3>
                                <p className="pricing-faq-a">
                                    Once, at the start of the period. There is no automatic renewal and no
                                    card kept on file. Nothing is taken again until the period ends.
                                </p>
                            </div>

                            <div className="pricing-faq-item">
                                <h3 className="pricing-faq-q">What counts as a table?</h3>
                                <p className="pricing-faq-a">
                                    Every table connected to the system, whatever the game: snooker,
                                    English pool, US pool or Chinese pool. Screens, phones and staff
                                    accounts are never counted.
                                </p>
                            </div>

                            <div className="pricing-faq-item">
                                <h3 className="pricing-faq-q">Will your prices go up?</h3>
                                <p className="pricing-faq-a">
                                    Your price per table is locked for the whole period you have paid for.
                                    If it changes afterwards, you will know before you renew.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="pricing-close">
                    <div className="pricing-container pricing-narrow">
                        <h2 className="pricing-close-title">Ready to set your club up?</h2>
                        <p className="pricing-close-desc">
                            Tell us how many tables you have and we will get your club running on
                            CueClub Manager.
                        </p>
                        <div className="pricing-close-actions">
                            <Link to="/login" className="pricing-btn pricing-btn-light pricing-btn-lg">
                                Get started
                            </Link>
                            <Link to="/" className="pricing-btn pricing-btn-outline pricing-btn-lg">
                                Back to site
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="pricing-footer">
                <div className="pricing-container pricing-narrow">
                    <p>Prices in US dollars ($). You pay in dollars, with no conversion.</p>
                </div>
            </footer>
        </div>
    );
};

export default Pricing;
